import type { LeadMatch, MatchReason } from "../../../Leads/api/services.types.ts"
import {
  type CustomerMatchCandidate,
  type TypedCustomerHandles,
  candidateKey,
  isEmailMatch,
  isPhoneMatch
} from "./customer-match.ts"

interface MatchedFields {
  email: boolean
  phone: boolean
}

interface MergedDuplicateMatch {
  kind: "merged"
  key: string
  customer: CustomerMatchCandidate
  lead: LeadMatch
  matchedFields: MatchedFields
}

interface CustomerDuplicateMatch {
  kind: "customer"
  key: string
  customer: CustomerMatchCandidate
  matchedFields: MatchedFields
}

interface LeadDuplicateMatch {
  kind: "lead"
  key: string
  lead: LeadMatch
}

type UnifiedDuplicateMatch =
  | MergedDuplicateMatch
  | CustomerDuplicateMatch
  | LeadDuplicateMatch

// A shared name is evidence, never identity (docs/kb/decisions/2026-08-27-
// duplicate-lead-warning-is-a-second-panel.md), so it is never strong enough to
// say a customer row and a lead row describe the same person. The backend's own
// lead-match lookup carries no customer id for the same reason — see
// ezbuild_back/web_app/apps/crm/lead_matches.py — so an exact-handle reason
// match is the only correlation signal either side of the wire can offer.
const STRONG_REASONS: MatchReason[] = ["email", "phone"]

function matchedFieldsOf(
  candidate: Pick<CustomerMatchCandidate, "email" | "phone">,
  typed: TypedCustomerHandles
): MatchedFields {
  return {
    email: isEmailMatch(typed.email, candidate.email),
    phone: isPhoneMatch(typed.phone, candidate.phone)
  }
}

function leadKey(lead: LeadMatch, index: number): string {
  return lead.id ? `id:${lead.id}` : `unreachable:${index}`
}

/**
 * One row per real-world duplicate signal, ranked lead-bearing first (the
 * larger news), so the panel that renders this list is never asked to show two
 * cards about the same match. A customer row and a lead row are folded into one
 * `merged` row only when a strong (email/phone) reason ties them together;
 * everything weaker renders as its own, unmerged row exactly as it did before.
 */
function buildUnifiedDuplicateMatches(
  customerMatches: CustomerMatchCandidate[],
  leadMatches: LeadMatch[],
  typed: TypedCustomerHandles
): UnifiedDuplicateMatch[] {
  const unpaired = leadMatches.map((lead, index) => ({
    lead,
    key: leadKey(lead, index)
  }))

  const takeLeadFor = (fields: MatchedFields) => {
    const index = unpaired.findIndex(
      ({ lead }) =>
        (lead.reason === "email" && fields.email) ||
        (lead.reason === "phone" && fields.phone)
    )
    return index === -1 ? null : unpaired.splice(index, 1)[0]
  }

  const rows: UnifiedDuplicateMatch[] = customerMatches.map((customer) => {
    const matchedFields = matchedFieldsOf(customer, typed)
    const key = `c:${candidateKey(customer)}`
    const paired = STRONG_REASONS.includes(customer.reason)
      ? takeLeadFor(matchedFields)
      : null

    return paired
      ? { kind: "merged", key, customer, lead: paired.lead, matchedFields }
      : { kind: "customer", key, customer, matchedFields }
  })

  for (const { lead, key } of unpaired) {
    rows.push({ kind: "lead", key: `l:${key}`, lead })
  }

  const rank = (row: UnifiedDuplicateMatch) => (row.kind === "customer" ? 1 : 0)
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
    .map(({ row }) => row)
}

/**
 * Whether a row's own signal is strong enough to stand alone as the answer,
 * rather than one of several names that merely share a search term. A merged
 * row is strong by construction — pairing already required it.
 */
function isStrongRow(row: UnifiedDuplicateMatch): boolean {
  if (row.kind === "merged") return true
  const reason = row.kind === "customer" ? row.customer.reason : row.lead.reason
  return STRONG_REASONS.includes(reason)
}

export {
  type CustomerDuplicateMatch,
  type LeadDuplicateMatch,
  type MatchedFields,
  type MergedDuplicateMatch,
  type UnifiedDuplicateMatch,
  buildUnifiedDuplicateMatches,
  isStrongRow,
  matchedFieldsOf
}
